import React, { useEffect, useRef, useState } from "react"
import { TAbrandUserCategories, TAgetUsersByCategories } from "../../../services/userAPI"
import { isAbortError } from "../../../types/error"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch, faSpinner, faTimes, faChevronDown } from "@fortawesome/free-solid-svg-icons"

interface SubCategoryOption {
  name?: string
  category?: string
  count: number
}

interface CategoryGroup {
  main_category: string
  sub_categories: SubCategoryOption[]
}

const UsersByCategories = () => {
  const abortControllerRef = useRef<AbortController | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [categorySearch, setCategorySearch] = useState("")
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])

  const [platform, setPlatform] = useState("insta")
  const [confidence, setConfidence] = useState(0.85)
  const [country, setCountry] = useState("")
  const [verification, setVerification] = useState("")

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await TAbrandUserCategories()
        if (response?.data) {
          setCategoryGroups(response.data)
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Error fetching categories:", err)
      }
    }

    fetchCategories()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const getSubCategoryName = (sub: SubCategoryOption) => sub.name || sub.category || ""

  const toggleCategory = (categoryName: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((c) => c !== categoryName)
      }
      return [...prev, categoryName]
    })
  }

  const toggleMainCategory = (mainCategory: string) => {
    const group = categoryGroups.find((g) => g.main_category === mainCategory)
    if (!group) {
      return
    }

    const subNames = group.sub_categories.map(getSubCategoryName).filter(Boolean)
    const allSelected = subNames.every((name) => selectedCategories.includes(name))

    if (allSelected) {
      setSelectedCategories((prev) => prev.filter((c) => !subNames.includes(c)))
      return
    }

    setSelectedCategories((prev) => {
      const newList = [...prev]
      subNames.forEach((name) => {
        if (!newList.includes(name)) {
          newList.push(name)
        }
      })
      return newList
    })
  }

  const toggleGroupExpand = (mainCategory: string) => {
    setExpandedGroups((prev) => {
      if (prev.includes(mainCategory)) {
        return prev.filter((g) => g !== mainCategory)
      }
      return [...prev, mainCategory]
    })
  }

  const removeCategory = (categoryName: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== categoryName))
  }

  const clearAllCategories = () => {
    setSelectedCategories([])
  }

  const filteredGroups = categoryGroups
    .map((group) => {
      const searchLower = categorySearch.toLowerCase()
      const matchMain = group.main_category.toLowerCase().includes(searchLower)
      const filteredSubs = group.sub_categories.filter((sub) => {
        const name = getSubCategoryName(sub).toLowerCase()
        return matchMain || name.includes(searchLower)
      })
      return { ...group, sub_categories: filteredSubs }
    })
    .filter((group) => group.sub_categories.length > 0)

  const buildParams = () => {
    const params = new URLSearchParams()
    params.set("categories", selectedCategories.join(","))
    params.set("platform", platform)
    params.set("confidence", confidence.toString())

    if (country) {
      params.set("country", country)
    }
    if (verification) {
      params.set("verification", verification)
    }

    return params.toString()
  }

  const fetchData = async () => {
    if (selectedCategories.length === 0) {
      setError("Lütfen en az bir kategori seçin.")
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    setSearched(true)

    try {
      const response = await TAgetUsersByCategories(buildParams(), abortControllerRef.current.signal)
      const responseData = response?.data || response?.users || []
      const users = Array.isArray(responseData) ? responseData : []
      setTotalRecords(users.length)
    } catch (err: any) {
      if (!isAbortError(err)) {
        setError(err?.message || "Bir hata oluştu.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    fetchData()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch()
    }
  }

  return (
    <div className="panel px-4 py-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Kategoriye Göre Kullanıcılar</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategoriler <span className="text-red-500">*</span>
            </label>

            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedCategories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                  >
                    {cat}
                    <button
                      type="button"
                      onClick={() => removeCategory(cat)}
                      className="hover:text-indigo-600"
                    >
                      <FontAwesomeIcon icon={faTimes} className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearAllCategories}
                  className="text-xs text-red-500 hover:text-red-700 ml-1"
                >
                  Tümünü Temizle
                </button>
              </div>
            )}

            <div className="relative">
              <button
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white flex items-center justify-between"
              >
                <span className="text-gray-500">
                  {selectedCategories.length === 0
                    ? "Kategori seçin..."
                    : `${selectedCategories.length} kategori seçildi`}
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`w-3 h-3 text-gray-400 transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      placeholder="Kategori ara..."
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      autoFocus
                    />
                  </div>

                  <div className="overflow-y-auto max-h-64">
                    {filteredGroups.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        Kategori bulunamadı
                      </div>
                    ) : (
                      filteredGroups.map((group) => {
                        const subNames = group.sub_categories
                          .map(getSubCategoryName)
                          .filter(Boolean)
                        const allSelected = subNames.every((name) =>
                          selectedCategories.includes(name),
                        )
                        const someSelected =
                          !allSelected &&
                          subNames.some((name) => selectedCategories.includes(name))
                        const isExpanded = expandedGroups.includes(group.main_category)

                        return (
                          <div
                            key={group.main_category}
                            className="border-b border-gray-50 last:border-0"
                          >
                            <div className="flex items-center px-3 py-2 hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate = someSelected
                                  }
                                }}
                                onChange={() => toggleMainCategory(group.main_category)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mr-2 cursor-pointer"
                              />
                              <button
                                type="button"
                                onClick={() => toggleGroupExpand(group.main_category)}
                                className="flex-1 text-left text-sm font-semibold text-gray-800 flex items-center justify-between"
                              >
                                <span>{group.main_category}</span>
                                <span className="text-xs text-gray-400 font-normal">
                                  {subNames.filter((n) => selectedCategories.includes(n)).length}/
                                  {subNames.length}
                                  <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={`ml-1.5 w-2.5 h-2.5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  />
                                </span>
                              </button>
                            </div>

                            {isExpanded && (
                              <div className="pl-6 pb-1">
                                {group.sub_categories.map((sub) => {
                                  const name = getSubCategoryName(sub)
                                  if (!name) {
                                    return null
                                  }
                                  const isSelected = selectedCategories.includes(name)
                                  return (
                                    <label
                                      key={name}
                                      className="flex items-center px-3 py-1 hover:bg-gray-50 cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleCategory(name)}
                                        className="w-3.5 h-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mr-2 cursor-pointer"
                                      />
                                      <span className="text-sm text-gray-700">{name}</span>
                                      <span className="ml-auto text-xs text-gray-400">
                                        {sub.count}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            >
              <option value="insta">Instagram</option>
              <option value="insta-reels">Instagram Reels</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Güven Eşiği: {(confidence * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={confidence}
              onChange={(e) => setConfidence(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ülke</label>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ör: Turkey"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Doğrulama</label>
            <select
              value={verification}
              onChange={(e) => setVerification(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            >
              <option value="">Tümü</option>
              <option value="true">Doğrulanmış</option>
              <option value="false">Doğrulanmamış</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
              {loading ? "Aranıyor..." : "Ara"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {searched && !loading && !error && (
        <div className="bg-indigo-50 text-indigo-700 px-4 py-3 rounded-lg font-medium">
          Toplam: {totalRecords.toLocaleString("tr-TR")} kullanıcı
        </div>
      )}
    </div>
  )
}

export default UsersByCategories
